"use client";

import { useEffect, useMemo, useState } from "react";
import { LocaleLink } from "@/components/i18n/LocaleLink";
import type { Card } from "@/lib/catalog/grouping";
import {
  boxCapacityForLength,
  effectiveDefaultLength,
  pricePerStem,
  stemsInBox,
} from "@/lib/catalog/pricing";
import { getDefaultBoxUnit } from "@/lib/catalog/box-unit-defaults";
import { addToBox } from "@/lib/cart/cart";
import type { Cart } from "@/lib/cart/types";
import { readCart, writeCart } from "@/lib/cart/storage";
import { readVarietyPref, writeVarietyPref } from "@/lib/catalog/preferences";
import { CATALOG_STRINGS } from "@/lib/i18n/catalog-strings";
import type { Locale } from "@/lib/i18n/getLocale";
import { GradientPlaceholder } from "./GradientPlaceholder";
import { ColorChipButton, ColorPickerInline } from "./ColorPickerInline";
import { mixAllowedForUnit, leadColor, colorForUnit } from "@/lib/catalog/color-selection";
import { usePriceGate } from "@/components/auth/usePriceGate";
import { SignInForPricesCTA } from "./SignInForPricesCTA";
import { useRouter } from "next/navigation";
import { isSoldOut, varietyKey } from "@/lib/availability/keys";

const CATEGORY_LABEL = {
  rose: "Rose",
  "garden-spray-rose": "Garden",
  hydrangea: "Hydrangea",
  specialty: "Specialty",
  "filler-green": "Filler",
} as const;

// DIRECTIVE #019 (BUG 3): post-Step-8.1 specialty + filler-green
// varieties carry `lengths: []` since they price flat per stem. The
// previous defaultLength returned `undefined` for them, which then
// fed pricePerStem() and yielded $0.00 in the row. Delegate to
// effectiveDefaultLength which falls back to the first pricePerStem
// entry (convention: length=60) for length-agnostic categories.
function defaultLength(card: Card): number {
  return effectiveDefaultLength(card.variants[0]);
}

function colorTitle(c: string): string {
  if (c === "mix") return "Mix";
  return c
    .split("-")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

function resolveStemPrice(
  variety: Card["variants"][number],
  length: number,
  colorChoice?: string,
): number {
  const fallback = pricePerStem(variety, length) ?? 0;
  if (!colorChoice || !variety.priceByColor) return fallback;
  if (colorChoice === "mix") {
    const all = Object.values(variety.priceByColor);
    if (all.length > 0) return Math.min(...all);
    return fallback;
  }
  return variety.priceByColor[colorChoice] ?? fallback;
}

export function PowerBuyerRow({
  card,
  globalUnit,
  onAdded,
  onWarn,
  initialLength,
  initialColor,
  initialQty,
  locale = "en",
  soldOutKeys,
}: {
  card: Card;
  globalUnit: "bunch" | "box";
  onAdded: (msg: string, slug?: string) => void;
  onWarn?: (msg: string) => void;
  initialLength?: number;
  initialColor?: string;
  initialQty?: number;
  locale?: Locale;
  // #452: active sold-out keys (variety/color/length scopes). Empty = all
  // available. Same shape the detail page threads to DetailPricingPanel.
  soldOutKeys?: string[];
}) {
  const variety = card.variants[0];
  const isHydrangea = variety.purchaseUnit === "box";
  // #63 box_stem: hydrangea sells by the STEM; grade sets the box size. These
  // rows show a grade selector + stem stepper (no Box/Bunch toggle) and add
  // loose stems. Everything else (roses, specialty) is unchanged.
  const isBoxStem = variety.saleMode === "box_stem";
  // #63 Phase 1.1: box_stem (hydrangea) is driven by the SAME global Box/Bunch
  // toggle as every other variety - Bunch -> loose STEMS, Box -> whole grade
  // boxes. Non-box_stem behavior is unchanged.
  const effectiveUnit: "bunch" | "box" | "stem" = isBoxStem
    ? globalUnit === "box"
      ? "box"
      : "stem"
    : isHydrangea
      ? "box"
      : globalUnit;
  const colorOptions = variety.colorOptions ?? [];
  const hasColorPicker = colorOptions.length > 0;

  // #468 (Alexis): by-bunch color rows DEFAULT to the variety's LEAD color, never
  // Mix - Mix is a by-box option only (a mixed-color box). Per-variety memory
  // (section 7c) still overrides, but a saved "mix" is coerced to lead in bunch
  // mode by the effect below.
  const saved =
    typeof window !== "undefined" ? readVarietyPref(variety.slug) : null;
  const [color, setColor] = useState<string>(() => {
    if (initialColor) return initialColor;
    if (saved?.color) return saved.color;
    return hasColorPicker ? leadColor(variety) : "";
  });
  const [length, setLength] = useState<number>(
    initialLength ?? saved?.length ?? defaultLength(card),
  );
  const [qty, setQty] = useState<number>(initialQty ?? saved?.qty ?? 1);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  // #63 Phase 1.1: selected grade SKU (each grade is a separate variant on the
  // card). `activeVariety` is what price/capacity/add resolve against; for
  // non-box_stem rows it is just variants[0], so downstream is byte-identical.
  const STEM_STEP = 10;
  const [gradeSlug, setGradeSlug] = useState<string>(() => card.variants[0].slug);
  const activeVariety = isBoxStem
    ? card.variants.find((v) => v.slug === gradeSlug) ?? card.variants[0]
    : variety;
  // #63 Phase 1.1: qty semantics follow the mode - stems (step 10, min 10) in
  // Bunch mode, boxes (step 1, min 1) in Box mode. Non-box_stem rows keep 1/1.
  const stemMode = effectiveUnit === "stem";
  const qtyStep = stemMode ? STEM_STEP : 1;
  const qtyMin = stemMode ? STEM_STEP : 1;
  const qtyMax = stemMode ? 990 : 99;

  // #468 (Alexis): Mix is by-box ONLY. Coerce Mix -> lead color whenever the
  // effective unit is bunch (a saved Mix pref, or Mix picked in box then toggled
  // to Bunch) so a single bunch is never "Mix".
  useEffect(() => {
    if (effectiveUnit === "bunch" && color === "mix") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setColor(leadColor(variety));
    }
  }, [effectiveUnit, color, variety]);

  // #63 Phase 1.1: keep qty valid when the global toggle flips a hydrangea row
  // between stems (min 10) and whole boxes (min 1).
  useEffect(() => {
    if (!isBoxStem) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setQty(stemMode ? STEM_STEP : 1);
  }, [stemMode, isBoxStem]);

  // DIRECTIVE #025: anonymous users see no prices + the "+" button
  // routes them to /sign-in?redirect=<current> instead of adding.
  const priceGate = usePriceGate();
  const router = useRouter();
  const canSeePrices = priceGate.ready && priceGate.canSeePrices;

  // #452: sold-out state via the shared resolver, mirroring DetailPricingPanel.
  // varietySoldOut greys the whole row + always disables Add; selectionSoldOut
  // reflects the current color/length pick (used for the Add gate); per-chip
  // greying uses isSoldOut for each length. Only blocks a signed-in buyer
  // (anon is already routed to sign-in); the server order/cart gate is the net.
  const keySet = useMemo(() => new Set(soldOutKeys ?? []), [soldOutKeys]);
  // #63 Phase 1.1: sold-out resolves against the ACTIVE grade SKU for hydrangea
  // (each grade is its own slug); activeVariety === variety otherwise.
  const varietySoldOut = keySet.has(varietyKey(activeVariety.slug));
  const selectionSoldOut = isSoldOut(keySet, {
    slug: activeVariety.slug,
    colorChoice: color || null,
    length: isHydrangea ? null : length,
  });
  const blockSoldOut = canSeePrices && selectionSoldOut;
  const rowSoldOut = canSeePrices && varietySoldOut;
  const colorSoldOut =
    canSeePrices &&
    !!color &&
    isSoldOut(keySet, { slug: activeVariety.slug, colorChoice: color, length: null });

  const stem = resolveStemPrice(activeVariety, length, color || undefined);
  const cap = boxCapacityForLength(activeVariety, length);
  // DIRECTIVE #023: universal QB default with per-variety memory. The
  // legacy hydrangea-only branch (Step 6.7.1) is subsumed since the
  // universal rule returns QB for hydrangea naturally. Saved memory
  // (saved.boxCode below) wins over the default.
  const code = cap
    ? getDefaultBoxUnit(activeVariety, saved?.boxCode ?? null)
    : null;
  const stemsBox = cap && code ? stemsInBox(cap, code) ?? 0 : 0;
  const stemsBunch = activeVariety.stemsPerBunch ?? 0;
  // #63 Phase 1.1: hydrangea shows "$X/stem" (price cell) in Bunch/stem mode and
  // "$Y/box" for a whole grade box; other varieties are unchanged.
  const subText = isBoxStem
    ? effectiveUnit === "box" && stemsBox
      ? `$${(stem * stemsBox).toFixed(2)}/box`
      : ""
    : effectiveUnit === "bunch"
      ? activeVariety.stemsPerBunch !== null
        ? `$${(stem * stemsBunch).toFixed(2)}/bunch`
        : "Sold by box"
      : code
        ? `$${(stem * stemsBox).toFixed(2)}/${code}`
        : "";

  const colorMeta =
    colorOptions.length > 0
      ? `${colorOptions.length} colors`
      : variety.colorTag.replace(/-/g, " ");
  const packMeta = variety.stemsPerBunch
    ? `${variety.stemsPerBunch}/bunch`
    : code
      ? `${stemsBox}/${code}`
      : "";
  const metaLine = `${CATEGORY_LABEL[variety.category]} * ${colorMeta} * ${packMeta}`;

  function handleAdd(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    // DIRECTIVE #025: anonymous users get routed to sign-in; their click
    // intent carries forward via the redirect query param.
    if (priceGate.ready && !priceGate.canSeePrices) {
      const here = `/catalog?view=power-buyer`;
      router.push(`/sign-in?redirect=${encodeURIComponent(here)}`);
      return;
    }
    // #452: a sold-out selection (whole variety, this color, or this length)
    // cannot be added from the list.
    if (blockSoldOut) return;
    // Step 6.7.3.1 / AC186: per-box loop. Same root cause as the
    // detail page (6.7.3 commit e8cf668) and popover (6.7.3.1 commit
    // 2): Box mode with qty > 2 produces over-cap single-line stems,
    // engine throws, React swallows. Power Buyer's qty stepper goes
    // to 99, so this is a real path even in fast-add mode.
    const iterations = effectiveUnit === "box" ? qty : 1;
    const perIterQty = effectiveUnit === "box" ? 1 : qty;
    let placedCart: Cart | null = null;
    let mixConflict = false;
    try {
      let runningCart = readCart();
      for (let i = 0; i < iterations; i++) {
        const outcome = addToBox(runningCart, {
          variety: activeVariety,
          length: isHydrangea ? undefined : length,
          unit: effectiveUnit,
          quantity: perIterQty,
          colorChoice: colorForUnit(activeVariety, color, effectiveUnit) || undefined,
          boxCode: effectiveUnit === "box" ? code ?? undefined : undefined,
        });
        if (outcome.kind === "added") {
          runningCart = outcome.cart;
          continue;
        }
        // Mix conflict on direct-add: silently open a new box and
        // raise the soft warning flag per spec 6.7.1 section 4c.
        mixConflict = true;
        const retry = addToBox(
          runningCart,
          {
            variety: activeVariety,
            length: isHydrangea ? undefined : length,
            unit: effectiveUnit,
            quantity: perIterQty,
            colorChoice: colorForUnit(activeVariety, color, effectiveUnit) || undefined,
            boxCode: effectiveUnit === "box" ? code ?? undefined : undefined,
          },
          { forceNewBox: true },
        );
        if (retry.kind === "added") runningCart = retry.cart;
      }
      placedCart = runningCart;
    } catch (err) {
      // Extreme Bunch qty (>HB cap) can still trip the engine. Surface
      // via the existing onAdded toast.
      const msg = err instanceof Error ? err.message : String(err);
      onAdded(`Couldn't add: ${msg}`);
      return;
    }
    if (placedCart) {
      writeCart(placedCart);
      // Step 6.7 section 7b: persist this choice for next visit.
      writeVarietyPref(variety.slug, {
        color: color || undefined,
        length: isHydrangea ? undefined : length,
        qty,
        // #63 box_stem: the saved unit pref only restores the Box/Bunch toggle
        // default; a "stem" add corresponds to Bunch mode for hydrangea.
        unit: effectiveUnit === "stem" ? "bunch" : effectiveUnit,
        // 12.8: persist the box size too. writeVarietyPref REPLACES the whole
        // record, so omitting boxCode here silently erased a box size the
        // buyer had explicitly picked on the detail page - their next
        // Power-Buyer add of that variety reverted to the QB default.
        boxCode: code ?? undefined,
      });
      const colorPart = color ? colorTitle(color) : "";
      const dict = CATALOG_STRINGS[locale];
      // #63 Phase 1.1: box_stem toasts name the grade + the per-mode unit
      // ("N stems" / "N box(es)"); other varieties keep the localized strings.
      const gradeWord = isBoxStem ? (activeVariety.grade ?? "") : "";
      const message = isBoxStem
        ? effectiveUnit === "stem"
          ? `Added ${qty} stems - ${gradeWord} ${card.name}`.replace(/ +/g, " ").trim()
          : `Added ${qty} box${qty === 1 ? "" : "es"} - ${gradeWord} ${card.name}`
              .replace(/ +/g, " ")
              .trim()
        : effectiveUnit === "bunch"
          ? dict.addedNBunchesOf(qty, card.name, colorPart)
          : dict.addedNBoxesOf(qty, code ?? "box", card.name, colorPart);
      onAdded(message, activeVariety.slug);
      if (mixConflict && onWarn) {
        onWarn(dict.mixConflictOpenedSecond(code ?? "QB", card.name));
      }
      setJustAdded(true);
      setTimeout(() => setJustAdded(false), 1500);
    }
  }

  // #468 mobile Power Buyer: the color chip / length chips / hydrangea lock,
  // shared by the desktop chip column AND the compact mobile row (mockup:
  // mobile_powerbuyer_color_mockup.html). `mobile` only varies testids + popover
  // anchoring; the state (color, length, open) is shared. Mix is by-box only, so
  // the color chip defaults to the LEAD color and offers Mix only in Box mode.
  function chipControl(mobile: boolean) {
    if (hasColorPicker) {
      return (
        <span
          data-sold-out={colorSoldOut ? "true" : undefined}
          className={"relative " + (colorSoldOut ? "opacity-40" : "")}
        >
          <ColorChipButton
            active={color || leadColor(variety)}
            testId={mobile ? "power-buyer-row-color-chip-mobile" : "power-buyer-row-color-chip"}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setColorPickerOpen((v) => !v);
            }}
          />
          {colorPickerOpen && (
            <ColorPickerInline
              options={colorOptions}
              active={color || leadColor(variety)}
              allowMix={mixAllowedForUnit(variety, effectiveUnit)}
              align={mobile ? "right" : "left"}
              testId={mobile ? "color-picker-inline-mobile" : "color-picker-inline"}
              onPick={(c) => {
                setColor(c);
                setColorPickerOpen(false);
              }}
              onClose={() => setColorPickerOpen(false)}
            />
          )}
        </span>
      );
    }
    if (isBoxStem) {
      // #63 Phase 1.1: grade selector lives in the SAME chip slot as the length
      // chips, styled identically. Label only (Select/Premium/Mini) - grade sets
      // the box size. Each grade is a separate SKU (card.variants).
      return (
        <span className="flex gap-1">
          {card.variants.map((gv) => {
            const on = gv.slug === activeVariety.slug;
            const gvSoldOut =
              canSeePrices &&
              isSoldOut(keySet, { slug: gv.slug, colorChoice: null, length: null });
            return (
              <button
                key={gv.slug}
                type="button"
                data-testid={`power-buyer-row-grade-${mobile ? "mobile-" : ""}${gv.grade ?? gv.slug}`}
                data-selected={on || undefined}
                aria-pressed={on}
                onClick={(e) => {
                  e.stopPropagation();
                  setGradeSlug(gv.slug);
                }}
                className={
                  (on
                    ? "rounded border border-brand-teal bg-brand-teal px-1.5 py-0.5 text-[10px] font-semibold text-white"
                    : "rounded border border-brand-border px-1.5 py-0.5 text-[10px] font-semibold text-brand-text-muted hover:border-brand-teal") +
                  (gvSoldOut ? " line-through opacity-40" : "")
                }
              >
                {gv.grade ?? "Grade"}
              </button>
            );
          })}
        </span>
      );
    }
    if (isHydrangea) {
      return globalUnit === "bunch" ? (
        <span
          data-testid={mobile ? "power-buyer-row-lock-mobile" : "power-buyer-row-lock"}
          className="whitespace-nowrap rounded bg-brand-bg-zebra px-1.5 py-0.5 text-[10px] font-semibold text-brand-text-muted"
        >
          {code ?? "Box"} only
        </span>
      ) : null;
    }
    return (
      <span className="flex gap-1">
        {variety.lengths.map((l) => {
          const isActive = l === length;
          const chipSoldOut =
            canSeePrices &&
            isSoldOut(keySet, {
              slug: variety.slug,
              colorChoice: color || null,
              length: l,
            });
          return (
            <button
              key={l}
              type="button"
              data-testid={`power-buyer-row-chip-${mobile ? "mobile-" : ""}${l}`}
              data-sold-out={chipSoldOut ? "true" : undefined}
              aria-pressed={isActive}
              onClick={(e) => {
                e.stopPropagation();
                setLength(l);
              }}
              className={
                (isActive
                  ? "rounded border border-brand-teal bg-brand-teal px-1.5 py-0.5 text-[10px] font-semibold text-white"
                  : "rounded border border-brand-border px-1.5 py-0.5 text-[10px] font-semibold text-brand-text-muted hover:border-brand-teal") +
                (chipSoldOut ? " line-through opacity-40" : "")
              }
            >
              {l}
            </button>
          );
        })}
      </span>
    );
  }

  return (
    <div
      data-testid="power-buyer-row"
      data-slug={card.primarySlug}
      data-route-slug={card.routeSlug}
      data-purchase-unit={variety.purchaseUnit}
      data-just-added={justAdded || undefined}
      data-sold-out={rowSoldOut ? "true" : undefined}
      className={`grid grid-cols-[32px_1fr_56px_auto] items-center gap-2 border-b border-brand-border-soft px-3.5 py-2.5 text-[12px] cursor-pointer transition-colors hover:bg-brand-teal-soft even:bg-brand-bg-zebra md:grid-cols-[40px_1.5fr_130px_130px_110px_90px] md:gap-3.5 md:px-5 md:py-2.5 md:text-[13px] ${justAdded ? "bg-brand-teal-soft" : ""} ${rowSoldOut ? "opacity-50 grayscale" : ""}`}
    >
      {/* col 1: thumbnail */}
      <div className="h-7 w-7 overflow-hidden rounded-md md:h-9 md:w-9">
        <GradientPlaceholder
          variety={variety}
          photoComingSoonLabel=""
          variant="thumbnail"
          className="h-full w-full"
        />
      </div>

      {/* col 2: name + meta */}
      <LocaleLink
        href={`/catalog/${card.routeSlug}`}
        data-testid="power-buyer-row-name"
        className="block min-w-0 hover:text-brand-teal"
      >
        <span className="block truncate text-[13px] font-bold text-brand-text">
          {card.name}
        </span>
        <span
          data-testid="power-buyer-row-meta"
          className="block truncate text-[10px] text-brand-text-muted md:text-[11px]"
        >
          {justAdded ? "Added" : metaLine}
        </span>
      </LocaleLink>

      {/* col 3: chips/colors (desktop only) */}
      <div className="relative hidden md:flex md:flex-wrap md:gap-1">
        {hasColorPicker ? (
          <span
            data-sold-out={colorSoldOut ? "true" : undefined}
            className={colorSoldOut ? "opacity-40" : undefined}
          >
            <ColorChipButton
              active={color || leadColor(variety)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setColorPickerOpen((v) => !v);
              }}
            />
            {colorPickerOpen && (
              <ColorPickerInline
                options={colorOptions}
                active={color || leadColor(variety)}
                allowMix={mixAllowedForUnit(variety, effectiveUnit)}
                onPick={(c) => {
                  setColor(c);
                  setColorPickerOpen(false);
                }}
                onClose={() => setColorPickerOpen(false)}
              />
            )}
          </span>
        ) : isBoxStem ? (
          // #63 Phase 1.1: grade chips in the col-3 chip slot, styled like the
          // length chips (label only). Grade sets the box size.
          <span className="flex flex-wrap gap-1">
            {card.variants.map((gv) => {
              const on = gv.slug === activeVariety.slug;
              const gvSoldOut =
                canSeePrices &&
                isSoldOut(keySet, { slug: gv.slug, colorChoice: null, length: null });
              return (
                <button
                  key={gv.slug}
                  type="button"
                  data-testid={`power-buyer-row-grade-${gv.grade ?? gv.slug}`}
                  data-selected={on || undefined}
                  aria-pressed={on}
                  onClick={(e) => {
                    e.stopPropagation();
                    setGradeSlug(gv.slug);
                  }}
                  className={
                    (on
                      ? "rounded border border-brand-teal bg-brand-teal px-1.5 py-0.5 text-[10px] font-semibold text-white"
                      : "rounded border border-brand-border px-1.5 py-0.5 text-[10px] font-semibold text-brand-text-muted hover:border-brand-teal") +
                    (gvSoldOut ? " line-through opacity-40" : "")
                  }
                >
                  {gv.grade ?? "Grade"}
                </button>
              );
            })}
          </span>
        ) : isHydrangea ? (
          globalUnit === "bunch" ? (
            <span
              data-testid="power-buyer-row-lock"
              className="rounded bg-brand-bg-zebra px-1.5 py-0.5 text-[10px] font-semibold text-brand-text-muted"
            >
              {code ?? "Box"} only
            </span>
          ) : null
        ) : (
          variety.lengths.map((l) => {
            const isActive = l === length;
            // #452: grey this length chip if it (or the whole variety) is
            // sold out for the current color. Chip stays clickable so the
            // buyer sees which length is out; selecting it disables Add.
            const chipSoldOut =
              canSeePrices &&
              isSoldOut(keySet, {
                slug: variety.slug,
                colorChoice: color || null,
                length: l,
              });
            return (
              <button
                key={l}
                type="button"
                data-testid={`power-buyer-row-chip-${l}`}
                data-sold-out={chipSoldOut ? "true" : undefined}
                aria-pressed={isActive}
                onClick={(e) => {
                  e.stopPropagation();
                  setLength(l);
                }}
                className={
                  (isActive
                    ? "rounded border border-brand-teal bg-brand-teal px-1.5 py-0.5 text-[10px] font-semibold text-white"
                    : "rounded border border-brand-border px-1.5 py-0.5 text-[10px] font-semibold text-brand-text-muted hover:border-brand-teal") +
                  (chipSoldOut ? " line-through opacity-40" : "")
                }
              >
                {l}
              </button>
            );
          })
        )}
      </div>

      {/* col 4: price (DIRECTIVE #025: gated for anonymous users) */}
      <div className="text-right">
        {canSeePrices ? (
          <>
            <div
              data-testid="power-buyer-row-stem-price"
              className="text-[12px] font-bold leading-tight md:text-[14px]"
            >
              ${stem.toFixed(2)}
              <span className="text-[9px] font-normal text-brand-text-muted md:text-[11px]">
                /stem
              </span>
            </div>
            <div className="text-[10px] text-brand-text-muted md:text-[11px]">
              {subText}
            </div>
          </>
        ) : priceGate.ready ? (
          <div
            data-testid="power-buyer-row-signed-out"
            className="text-[10px] md:text-[11px]"
          >
            <SignInForPricesCTA variant="inline" />
          </div>
        ) : (
          <div
            aria-hidden="true"
            data-testid="power-buyer-row-price-skeleton"
            className="inline-block h-3 w-12 animate-pulse rounded bg-gray-100 md:h-4 md:w-16"
          />
        )}
      </div>

      {/* col 5: qty stepper (desktop only) */}
      <div className="hidden md:flex md:items-center md:justify-self-start md:overflow-hidden md:rounded md:border md:border-brand-border">
        <button
          type="button"
          data-testid="power-buyer-row-decrement"
          onClick={(e) => {
            e.stopPropagation();
            setQty((q) => Math.max(qtyMin, q - qtyStep));
          }}
          className="h-9 w-9 bg-brand-surface text-sm text-brand-text-muted hover:bg-brand-border"
        >
          -
        </button>
        <input
          data-testid="power-buyer-row-qty"
          aria-label="Quantity"
          value={qty}
          readOnly
          className="h-9 w-10 border-0 bg-white text-center text-base font-semibold"
        />
        <button
          type="button"
          data-testid="power-buyer-row-increment"
          onClick={(e) => {
            e.stopPropagation();
            setQty((q) => Math.min(qtyMax, q + qtyStep));
          }}
          className="h-9 w-9 bg-brand-surface text-sm text-brand-text-muted hover:bg-brand-border"
        >
          +
        </button>
      </div>

      {/* col 6: add (desktop) - DIRECT add, no popover.
          DIRECTIVE #025: anonymous users get the same button but
          click routes to /sign-in (per Q3 confirmed). Visual is a
          softer teal so the button reads "you can click, it's an
          auth step." */}
      <button
        type="button"
        data-testid="power-buyer-row-add"
        data-unit={effectiveUnit}
        data-auth-gated={priceGate.ready && !canSeePrices ? "true" : undefined}
        data-sold-out={blockSoldOut ? "true" : undefined}
        disabled={blockSoldOut}
        onClick={handleAdd}
        aria-label={
          blockSoldOut
            ? "Sold out"
            : !canSeePrices && priceGate.ready
              ? "Sign in to add"
              : undefined
        }
        className={
          "hidden rounded px-3.5 py-1.5 text-[12px] font-bold shadow-sm md:block " +
          (blockSoldOut
            ? "cursor-not-allowed bg-brand-border text-brand-text-muted"
            : "text-white " +
              (priceGate.ready && !canSeePrices
                ? "bg-brand-teal/70 hover:bg-brand-teal"
                : "bg-brand-teal hover:bg-brand-teal-dark"))
        }
      >
        {blockSoldOut
          ? "Sold out"
          : priceGate.ready && !canSeePrices
            ? "Sign in"
            : justAdded
              ? "Added"
              : "Add"}
      </button>

      {/* mobile-only cluster (mockup): color/length chip + "+". chipControl
          shows the color chip (defaults to the LEAD color, Mix only in Box) or
          the rose length chips; "+" adds the shown color. */}
      <div className="relative flex items-center justify-end gap-1.5 md:hidden">
        {chipControl(true)}
        <button
        type="button"
        data-testid="power-buyer-row-add-mobile"
        data-unit={effectiveUnit}
        data-auth-gated={priceGate.ready && !canSeePrices ? "true" : undefined}
        data-sold-out={blockSoldOut ? "true" : undefined}
        disabled={blockSoldOut}
        aria-label={
          blockSoldOut
            ? "Sold out"
            : !canSeePrices && priceGate.ready
              ? "Sign in to add"
              : `Add ${card.name}`
        }
        onClick={handleAdd}
        className={
          "flex h-9 w-9 items-center justify-center rounded-md text-base font-bold shadow-sm md:hidden " +
          (blockSoldOut
            ? "cursor-not-allowed bg-brand-border text-brand-text-muted"
            : "text-white " +
              (priceGate.ready && !canSeePrices ? "bg-brand-teal/70" : "bg-brand-teal"))
        }
      >
        {blockSoldOut ? "×" : "+"}
        </button>
      </div>
    </div>
  );
}
